import { useState, useRef, useEffect, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { get, post, del } from "@/api/client";

interface CopilotStatus {
  enabled: boolean;
  features: {
    chat: boolean;
    actions: boolean;
    search: boolean;
    cataloging: boolean;
  };
}

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

interface ChatResponse {
  conversationId: string;
  message: string;
  action?: string;
  actionParams?: Record<string, unknown>;
  requiresConfirmation: boolean;
  actionResult?: Record<string, unknown>;
  tokensUsed: number;
  error?: string;
}

export function CopilotPage() {
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingAction, setPendingAction] = useState<{
    action: string;
    params: Record<string, unknown>;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch Copilot status
  const { data: status } = useQuery<CopilotStatus>({
    queryKey: ["copilot-status"],
    queryFn: () => get<CopilotStatus>("/api/ai/copilot/status"),
  });

  // Start conversation mutation
  const startMutation = useMutation({
    mutationFn: () =>
      post<{ conversationId: string; message: string }>("/api/ai/copilot/conversations", {}),
    onSuccess: (data) => {
      setConversationId(data.conversationId);
      setMessages([
        {
          role: "assistant",
          content: data.message,
          timestamp: new Date().toISOString(),
        },
      ]);
    },
  });

  // Chat mutation
  const chatMutation = useMutation({
    mutationFn: (message: string) =>
      post<ChatResponse>(`/api/ai/copilot/conversations/${String(conversationId)}/messages`, {
        message,
      }),
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.message,
          timestamp: new Date().toISOString(),
        },
      ]);

      if (data.requiresConfirmation && data.action && data.actionParams) {
        setPendingAction({
          action: data.action,
          params: data.actionParams,
        });
      }
    },
  });

  // Confirm action mutation
  const confirmMutation = useMutation({
    mutationFn: (params: { action: string; params: Record<string, unknown> }) =>
      post<ChatResponse>(`/api/ai/copilot/conversations/${String(conversationId)}/confirm`, params),
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.message,
          timestamp: new Date().toISOString(),
        },
      ]);
      setPendingAction(null);
    },
  });

  // End conversation mutation
  const endMutation = useMutation({
    mutationFn: () => del(`/api/ai/copilot/conversations/${String(conversationId)}`),
    onSuccess: () => {
      setConversationId(null);
      setMessages([]);
      setPendingAction(null);
    },
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = useCallback(() => {
    if (!input.trim() || chatMutation.isPending) return;

    const userMessage = input.trim();
    setInput("");

    // Add user message optimistically
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
        timestamp: new Date().toISOString(),
      },
    ]);

    chatMutation.mutate(userMessage);
  }, [input, chatMutation]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleConfirm = () => {
    if (pendingAction) {
      confirmMutation.mutate(pendingAction);
    }
  };

  const handleCancel = () => {
    setPendingAction(null);
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: "Action annulée.",
        timestamp: new Date().toISOString(),
      },
    ]);
  };

  // Render disabled state
  if (!status?.enabled) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <div className="text-6xl">🤖</div>
        <h1 className="mt-4 text-2xl font-semibold text-gray-900 dark:text-white">Lumen Copilot</h1>
        <p className="mt-2 text-gray-500 dark:text-gray-400">
          L'assistant IA n'est pas activé. Configurez un fournisseur LLM pour l'utiliser.
        </p>
      </div>
    );
  }

  // Render start conversation
  if (!conversationId) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <div className="text-6xl">🤖</div>
        <h1 className="mt-4 text-2xl font-semibold text-gray-900 dark:text-white">Lumen Copilot</h1>
        <p className="mt-2 max-w-md text-center text-gray-500 dark:text-gray-400">
          Votre assistant bibliothécaire. Posez des questions, recherchez des documents, gérez les
          lecteurs — en langage naturel.
        </p>
        <button
          onClick={() => {
            startMutation.mutate();
          }}
          disabled={startMutation.isPending}
          className="mt-6 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {startMutation.isPending ? "Démarrage..." : "Démarrer une conversation"}
        </button>

        <div className="mt-8 grid max-w-2xl grid-cols-2 gap-4 text-sm">
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="font-medium text-gray-900 dark:text-white">🔍 Recherche</div>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              « Trouve les livres sur le jardinage »
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="font-medium text-gray-900 dark:text-white">👤 Lecteurs</div>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              « Montre le compte de Martin Dupont »
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="font-medium text-gray-900 dark:text-white">📚 Disponibilité</div>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              « Est-ce que "Le Petit Prince" est disponible ? »
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="font-medium text-gray-900 dark:text-white">📊 Statistiques</div>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              « Combien de prêts ce mois-ci ? »
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render conversation
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🤖</span>
          <div>
            <h1 className="font-semibold text-gray-900 dark:text-white">Lumen Copilot</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{messages.length} message(s)</p>
          </div>
        </div>
        <button
          onClick={() => {
            endMutation.mutate();
          }}
          className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          Nouvelle conversation
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  message.role === "user"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white"
                }`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {chatMutation.isPending && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-gray-100 px-4 py-2 dark:bg-gray-800">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
                  <div
                    className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                    style={{ animationDelay: "0.1s" }}
                  />
                  <div
                    className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                    style={{ animationDelay: "0.2s" }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Pending action confirmation */}
          {pendingAction && (
            <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
              <p className="font-medium text-yellow-900 dark:text-yellow-100">
                ⚠️ Action en attente de confirmation
              </p>
              <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                Action : {pendingAction.action}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={handleConfirm}
                  disabled={confirmMutation.isPending}
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {confirmMutation.isPending ? "Exécution..." : "✓ Confirmer"}
                </button>
                <button
                  onClick={handleCancel}
                  className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  ✗ Annuler
                </button>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-4 dark:border-gray-700">
        <div className="mx-auto flex max-w-3xl gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Posez votre question..."
            disabled={chatMutation.isPending}
            className="flex-1 rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || chatMutation.isPending}
            className="rounded-xl bg-blue-600 px-6 py-3 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}
